// Hard rule 7: closed periods are locked. Corrections post to current month as reversals.

import { PeriodLockedError } from '../errors.js';

export type PeriodStatus = 'open' | 'soft_closed' | 'closed';
export type BusinessDate = string & { readonly __brand: 'business_date' };
export type Month = string & { readonly __brand: 'month' }; // 'YYYY-MM'

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

export function businessDate(s: string): BusinessDate {
  if (!DATE_RE.test(s)) {
    throw new Error(`invalid business date "${s}", expected YYYY-MM-DD`);
  }
  return s as BusinessDate;
}

export function month(s: string): Month {
  if (!MONTH_RE.test(s)) {
    throw new Error(`invalid month "${s}", expected YYYY-MM`);
  }
  return s as Month;
}

export function monthOf(d: BusinessDate): Month {
  return d.slice(0, 7) as Month;
}

/** Financial year (India): April–March. Returns start year (e.g. 2025 for April 2025 → March 2026). */
export function financialYearOf(d: BusinessDate): number {
  const [y, m] = d.split('-').map(Number);
  return m! >= 4 ? y! : y! - 1;
}

export type GuardOutcome = 'allow' | 'warn' | 'block';

export function guardWrite(status: PeriodStatus): GuardOutcome {
  switch (status) {
    case 'open':
      return 'allow';
    case 'soft_closed':
      return 'warn';
    case 'closed':
      return 'block';
  }
}

/** Throws if the outcome is 'block'. Callers decide what 'warn' means (usually: log + proceed). */
export function assertWriteAllowed(m: Month, status: PeriodStatus): GuardOutcome {
  const outcome = guardWrite(status);
  if (outcome === 'block') throw new PeriodLockedError(m);
  return outcome;
}

// Test-only override for `todayIst()`. Set via the test-only route
// `POST /api/_test/set-today`. When set AND the caller does not pass an
// explicit `now`, `todayIst()` returns this string instead of the wall
// clock. Production code paths never pass `now`, so the override is
// transparent to tests.
let todayOverride: BusinessDate | null = null;

export function setTodayOverride(iso: string | null): void {
  if (iso === null) {
    todayOverride = null;
    return;
  }
  if (!DATE_RE.test(iso)) {
    throw new Error(`invalid today override "${iso}", expected YYYY-MM-DD`);
  }
  todayOverride = iso as BusinessDate;
}

/** Today's business date in IST as a YYYY-MM-DD string. */
export function todayIst(now?: Date): BusinessDate {
  if (now === undefined && todayOverride !== null) return todayOverride;
  const base = now ?? new Date();
  const istMs = base.getTime() + (5 * 60 + 30) * 60 * 1000;
  const d = new Date(istMs);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const da = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${da}` as BusinessDate;
}

/** Shift a business date by whole days. Negative goes backwards. */
export function addBusinessDays(date: BusinessDate, days: number): BusinessDate {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10) as BusinessDate;
}
